import api from 'api';
import type { AppId } from 'constants/known-apps';
import {
  ASANA,
  ASANA_SYNC,
  CLICKUP,
  customOptionApps,
  GOOGLE_CALENDAR,
  MONDAY,
  OFFICE365,
} from 'constants/known-apps';
import { HAS_CONNECTED_APP, SHOW_HOUR_EDITOR_ATTACHMENTS } from 'constants/user';
import { errorMessageFactory } from 'factories/api';
import { saveUserProperty } from 'redux/modules/user.actions';
import {
  getAppAccountEditorId,
  getAppEditorData,
  getAppEditorId,
  getAppsDraft,
} from 'redux/selectors/apps';
import { getUserPropertyAsBoolean } from 'redux/selectors/user';
import { TrackingActions } from 'tracking/actions';
import type { App, AppAccount, AppEntityImportRequest, AppOptionsResponse } from 'types/apps';
import { ScopeTypes } from 'types/apps';
import type { Thunk } from 'types/redux';
import * as actionTypes from './apps.types';
import { wrapFetching, wrapSaving } from './apps.types';
import { resetImportState, setImportingFrom, submittedImportData } from './integrations.types';
import { showToast } from './toasts.actions';

export const fetchApps = (): Thunk<Promise<Array<App>>> => {
  return async (dispatch, getState) => {
    const state = getState();
    const apps = await dispatch(wrapFetching(api.getApps(), 'apps'));

    const hasConnectedApp = apps.find(app => app.integrated);

    dispatch(actionTypes.fetchedApps(apps));

    // Mark user as having connected to an app
    if (hasConnectedApp) {
      if (!getUserPropertyAsBoolean(state, HAS_CONNECTED_APP)) {
        TrackingActions.connectedApp(hasConnectedApp.id);
        dispatch(saveUserProperty(SHOW_HOUR_EDITOR_ATTACHMENTS));
      }
      dispatch(saveUserProperty(HAS_CONNECTED_APP, true));
    }

    return apps;
  };
};

export function fetchAppAccounts(appId: number): Thunk<Promise<Array<AppAccount>>> {
  return async dispatch => {
    const accounts = await dispatch(wrapFetching(api.getAppAccounts(appId), 'accounts'));
    dispatch(
      actionTypes.fetchedAccounts({
        appId,
        accounts,
      })
    );

    return accounts;
  };
}

export function removeAppAccount(appId: number, appAccountId: number): Thunk<Promise<unknown>> {
  return async (dispatch, getState) => {
    const { apps } = getState();
    const app = apps.appsEntities[appId];
    const accounts = apps.accounts[appId];
    const account = accounts.find(item => item.id === appAccountId);

    if (account == null) {
      throw Error('Tried to remove app from account that does not exist');
    }

    dispatch(
      actionTypes.removeAccount({
        appId,
        accountId: appAccountId,
        disconnected: account.disconnected,
      })
    );

    const isAccountLevelApp = app.scope === ScopeTypes.account;

    try {
      await dispatch(
        wrapFetching(api.deleteAppAccount(appId, appAccountId, isAccountLevelApp), 'accounts')
      );
    } catch (e) {
      // Undo optimistic update
      dispatch(
        actionTypes.addAccount({
          appId,
          account,
        })
      );
      throw e;
    }

    // Disable the app if we're removing its last account.
    if (accounts.length === 1) {
      dispatch(actionTypes.removeApp(appId));
    }
    await dispatch(fetchApps());
  };
}

export function fetchAccountSubscriptions(accountId: number): Thunk<Promise<void>> {
  return async dispatch => {
    const subscriptions = await dispatch(
      wrapFetching(api.getAppAccountSubscriptions(accountId), 'subscriptions')
    );
    dispatch(
      actionTypes.fetchedSubscriptions({
        accountId,
        subscriptions,
      })
    );
  };
}

export function fetchDataForAppEditor(appId: number): Thunk<Promise<unknown>> {
  return (dispatch, getState) => {
    // Don't fetch accounts for apps that aren't integrated
    if (!getState().apps.appsEntities[appId].integrated) {
      return Promise.resolve();
    }

    return dispatch(fetchAppAccounts(appId)).then((accounts): Promise<unknown> => {
      if (accounts && accounts.some(account => account.objects)) {
        return Promise.all(accounts.map(({ id }) => dispatch(fetchAccountSubscriptions(id))));
      }

      return Promise.resolve();
    });
  };
}

export function refreshAppEditorAccounts(appId: number): Thunk<Promise<Array<AppAccount>>> {
  return (dispatch, getState) => {
    const oldAccounts = getState().apps.accounts[appId];

    return dispatch(fetchAppAccounts(appId)).then(accounts => {
      let newAccounts = accounts;

      // If we had accounts in store for this app, compare them to the ones in the response and
      // refresh subscriptions if any new ones were added.
      if (oldAccounts) {
        newAccounts = accounts.filter(
          account => !oldAccounts.find(oldAccount => oldAccount.id === account.id)
        );
      }

      if (newAccounts.length && newAccounts[0].objects) {
        return dispatch(fetchAccountSubscriptions(newAccounts[0].id)).then(() => newAccounts);
      }

      return Promise.resolve(newAccounts);
    });
  };
}

export function openAppAccountEditor(appAccountId: number): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const appId = getAppEditorId(state);
    const id = appId && state.apps.appsEntities[appId].id;
    if (!appId || !id) return;

    let options: actionTypes.AppImportOptions<any> | null =
      state.apps.accounts[appId]?.find(a => a.id === appAccountId)?.options ?? null;

    if (options === null) {
      if (id === MONDAY) {
        options = {
          ...actionTypes.mondaySourceOptions[actionTypes.DEFAULT_MONDAY_SOURCE],
          parent_source: 'workspace',
          sync_all: true,
        } as actionTypes.AppImportOptions<actionTypes.MondaySourceOptions>;
      }
      if (id === CLICKUP) {
        options = {
          ...actionTypes.clickUpSourceOptions[actionTypes.DEFAULT_CLICKUP_SOURCE],
          parent_source: 'parent',
          sync_all: true,
        } as actionTypes.AppImportOptions<actionTypes.ClickUpSourceOptions>;
      }
    }

    if ([CLICKUP, MONDAY, ASANA_SYNC, ASANA].includes(id) && options) {
      options = {
        ...options,
        sync_all: options.sync_all ?? true,
        ...(options.parent_source
          ? {}
          : {
              parent_source: id === MONDAY ? 'workspace' : id === CLICKUP ? 'parent' : 'board',
            }),
      };
    }

    let subscriptions = state.apps.subscriptions[appAccountId];

    // For Google Calendar and Office 365, pre-select all calendars by default only if none are currently selected (ususally after fresh connect)
    if ((id === GOOGLE_CALENDAR || id === OFFICE365) && subscriptions) {
      const hasAnySelectedCalendars = subscriptions.some(subscription => subscription.integrated);
      if (!hasAnySelectedCalendars) {
        subscriptions = subscriptions.map(subscription => ({
          ...subscription,
          integrated: true,
        }));
      }
    }

    dispatch(
      actionTypes.updateDraft({
        subscriptions,
        options,
      })
    );

    dispatch(actionTypes.toggleAccountEditor(appAccountId));
  };
}

export function toggleSubscriptionSelected(id: string) {
  return actionTypes.toggleDraftSubscriptionIntegrated({
    id,
  });
}

export function saveSubscriptions(): Thunk<Promise<unknown>> {
  return async (dispatch, getState) => {
    const accountId = getState().apps.appAccountEditorId;
    const subscriptions = getState().apps.draft.subscriptions || [];
    const activeSubscriptions = subscriptions.filter(subscription => subscription.integrated);

    if (accountId == null) {
      throw Error('expecting apps.appAccountEditorId to be set when saving subscriptions');
    }

    dispatch(actionTypes.toggleAccountEditor(null));

    dispatch(actionTypes.updateSubscriptions({ accountId, subscriptions }));

    // The backend will disable any subscriptions that aren't part of the update by toggling a flag
    // on them but the total number of objects in the response will remain the same.
    const response = await api.updateAccountSubscriptions(accountId, activeSubscriptions);
    dispatch(actionTypes.updateSubscriptions({ accountId, subscriptions: response }));
  };
}

export function setSearchFilter(value: string = '') {
  return actionTypes.updateSearchFilter(value);
}

export function toggleAppsListModal() {
  return actionTypes.toggleAppsListModal();
}

export function openAppEditor(id: number | AppId) {
  return actionTypes.toggleAppEditor(id);
}

export function closeAppEditor() {
  return actionTypes.toggleAppEditor(null);
}

export function closeAppAccountEditor() {
  return actionTypes.toggleAccountEditor(null);
}

// TODO: Could this be a depdendency injection pattern to explore, to facilitate easier testing of
// methods wrapping the API?
export const saveAppOptions =
  (injectedApi: Pick<typeof api, 'updateAppAccount'> = api): Thunk<Promise<void>> =>
  async (dispatch, getState) => {
    const state = getState();
    const { appId, displayName, id } = getAppEditorData(state);
    const appAccountId = getAppAccountEditorId(state);
    const draft = getAppsDraft(state);
    const options = {
      ...(draft.options || {}),
      sync_all: draft.options?.sync_all ?? false,
    } as actionTypes.AppImportOptions;

    if (!appId || !id || !appAccountId) {
      return;
    }

    try {
      // backend returns different payload when we update options..
      // @ts-expect-error
      const { id: jobId, details }: AppOptionsResponse = await dispatch(
        wrapSaving(
          injectedApi.updateAppAccount(appId, appAccountId, {
            options,
          }),
          'accounts'
        )
      );

      dispatch(
        actionTypes.updateAccount({
          appId,
          appAccountId,
          patch: { options },
        })
      );

      if (!options.sync_all && options.parent_source) {
        let entities = [];
        switch (options.parent_source) {
          case 'space':
            entities = details.spaces;
            break;
          case 'workspace':
            entities = details.workspaces.map(w => (w.id === null ? { ...w, id: -1 } : w)); //  Monday workspace id can be null, which means the root workspace
            break;
          case 'board':
            entities = details.boards;
            break;
          case 'project':
            entities = details.projects;
            break;
          case 'parent':
            entities = id === ASANA_SYNC ? details.workspaces : details.parents;
        }
        dispatch(
          actionTypes.initImportWizard({
            jobId,
            entities,
          })
        );
      } else {
        dispatch(startEntityImport(true));
      }

      dispatch(fetchApps());

      if (!customOptionApps.includes(id)) {
        dispatch(closeAppAccountEditor());

        dispatch(
          showToast({
            type: 'success',
            title: `Completed ${displayName} configuration`,
            message: `Your data from ${displayName} will now be synchronized into Timely.`,
          })
        );
      }
    } catch {
      dispatch(
        showToast({
          type: 'danger',
          message: `Failed to save ${displayName} settings`,
        })
      );
    }
  };

export const startEntityImport =
  (skipCall: boolean = false): Thunk =>
  async (dispatch, getState) => {
    const state = getState().apps;
    const appAccountId = state.appAccountEditorId;

    try {
      dispatch(actionTypes.setImportWizardStatus(actionTypes.ImportStatus.IN_PROGRESS));
      dispatch(setImportingFrom({ accountId: appAccountId! }));
      if (!skipCall) {
        const { selectedEntities, jobId } = state.import;
        const { parent_source } = state.draft.options as actionTypes.AppImportOptions;
        const payload: AppEntityImportRequest = {
          job_id: jobId!,
          // @ts-expect-error
          account_app: { parent_ids: selectedEntities.map(e => (e === -1 ? null : e)) }, // Workaround for Monday.com workspace id which could be null for root workspace
          parent_source,
        };
        await api.startEntityImport(appAccountId!, payload);
      }
      dispatch(submittedImportData({ accountId: appAccountId! }));
      dispatch(closeAppAccountEditor());
    } catch (e) {
      dispatch(actionTypes.setImportWizardStatus(actionTypes.ImportStatus.INITIALIZED));
      dispatch(resetImportState);
      dispatch(
        showToast({
          type: 'danger',
          message: errorMessageFactory(e),
        })
      );
    }
  };
export const setImportStatusFinished =
  (appAccountId: number): Thunk =>
  (dispatch, getState) => {
    const state = getState().apps;
    let appId: number | undefined;
    let appAccountIndex = -1;

    for (const [id, appAccounts] of Object.entries(state.accounts)) {
      appAccountIndex = appAccounts.findIndex(account => account.id === appAccountId);
      if (appAccountIndex !== -1) {
        appId = +id;
        break;
      }
    }

    if (appAccountIndex === -1 || !appId) {
      return;
    }

    dispatch(actionTypes.setImportStatusFinished({ appId, appAccountId }));

    const { display_name } = state.appsEntities[appId];

    dispatch(
      showToast({
        type: 'success',
        title: `Import from ${display_name} finished`,
        message: `Your data from ${display_name} is now available in Timely.`,
      })
    );
  };
